<script>
export default {
  name: 'FileUpload',
  props: {
    multiple: {
      type: Boolean,
      default: false,
    },
    height: {
      type: [String, Number],
      default: '250',
    },
    icon: {
      type: Boolean,
      default: false,
    },
    nonDraggable: {
      type: Boolean,
      default: false,
    },
    files: {
      type: Array,
      required: true,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dragover: false,
      uploadedFiles: [],
    }
  },
  watch: {
    files: {
      handler(newVal, oldVal) {
        this.uploadedFiles = newVal
      },
      immediate: true,
    },
  },
  methods: {
    removeFile(fileName) {
      // Find the index of the
      const index = this.uploadedFiles.findIndex(
        (file) => file.name === fileName
      )
      // If file is in uploaded files remove it
      if (index > -1) {
        this.uploadedFiles.splice(index, 1)
        this.submit()
      }
    },
    onDragover(e) {
      e.preventDefault()
      this.dragover = true
    },
    onDragenter(e) {
      e.preventDefault()
      this.dragover = true
    },
    onDragleave(e) {
      e.preventDefault()
      this.dragover = false
    },
    onDrop(e) {
      e.preventDefault()
      this.dragover = false
      if (!this.multiple && e.dataTransfer.files.length > 1) {
        console.warn('1 file at a time')
      }
      // Add each file to the array of uploaded files
      else {
        const files = e.dataTransfer.files
        for (let i = 0; i < files.length; i++) {
          this.uploadedFiles.push(files[i])
        }
        this.submit()
      }
    },
    openChooseFiles() {
      document.getElementById('upload-file').click()
    },
    chooseFiles(e) {
      this.dragover = false
      // If there are already uploaded files remove them
      if (this.uploadedFiles.length > 0) this.uploadedFiles = []
      // If user has uploaded multiple files but the component is not multiple throw error
      if (!this.multiple && e.target.files.length > 1) {
        console.warn('1 file at a time')
      } else {
        const files = e.target.files
        for (let i = 0; i < files.length; i++) {
          this.uploadedFiles.push(files[i])
        }
        this.submit()
      }
    },
    submit() {
      // If there aren't any files to be uploaded throw error
      if (!this.uploadedFiles.length > 0) {
        console.warn('no files')
      } else {
        // Send uploaded files to parent component
        this.$emit('update:files', this.uploadedFiles)
      }
    },
  },
}
</script>

<template>
  <v-sheet
    :class="dragover ? $style.fileContainer : ''"
    :min-height="height"
    rounded="xl"
    :disabled="disabled"
    class="pa-1 flex-column d-flex justify-space-between"
    color="background"
    v-on="
      !nonDraggable
        ? {
            drop: ($event) => onDrop($event),
            dragover: ($event) => onDragover($event),
            dragenter: ($event) => onDragenter($event),
            dragleave: ($event) => onDragleave($event),
          }
        : {}
    "
  >
    <v-row
      v-show="dragover"
      dense
      no-gutters
      align="center"
      justify="center"
      :class="$style.fileOverlayText"
    >
      <v-icon
        v-if="icon"
        color="primary"
        class="mt-8 mb-4"
        size="60"
        :disabled="disabled"
      >
        mdi-cloud-upload-outline
      </v-icon>
      <p> Drop your file(s) here </p>
    </v-row>
    <v-row
      v-if="!dragover"
      class="mt-2"
      align="start"
      justify="center"
      no-gutters
    >
      <p> Drop your file(s), or select them to upload. </p>
    </v-row>
    <v-row v-if="uploadedFiles.length > 0 && !dragover" no-gutters>
      <v-virtual-scroll
        class="pa-3"
        :items="uploadedFiles"
        :max-height="height - 100"
        item-height="50"
      >
        <template v-slot="{ item }">
          <v-list-item :key="item.name">
            <v-list-item-content>
              <v-list-item-title>
                {{ item.name }}
                <span class="ml-3 text--secondary"> {{ item.size }}</span>
              </v-list-item-title>
            </v-list-item-content>

            <v-list-item-action>
              <v-btn
                icon
                :disabled="disabled"
                @click.stop="removeFile(item.name)"
              >
                <v-icon> mdi-close-circle </v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </template>
      </v-virtual-scroll>
    </v-row>

    <v-row no-gutters class="mb-2 h-100" justify="center" align="end">
      <v-btn
        class="primary--text"
        outlined
        rounded
        :disabled="disabled"
        @click="openChooseFiles"
        >Upload <v-icon class="ml-2">mdi-cloud-upload-outline</v-icon></v-btn
      >
      <input
        id="upload-file"
        :disabled="disabled"
        :multiple="multiple"
        type="file"
        hidden
        @change="chooseFiles($event)"
      />
    </v-row>
  </v-sheet>
</template>

<style lang="scss" module>
@import '@design';

.container {
  height: 100%;
}

.fileContainer {
  background: $color-theme-light-upload !important;
}

.fileOverlayText {
  pointer-events: none;
}
</style>
